import { useId, useRef, useState } from "react";

export interface Series { name: string; color: string; values: (number | null)[] }
interface Props {
  labels: string[];          // short axis labels, one per point
  long?: string[];           // readout labels
  series: Series[];
  fmt(v: number): string;
  min?: number; max?: number; pad?: number; floor?: number; ceil?: number; ref?: number;
  bars?: boolean;
  title: string;
}

/** Dependency-free SVG chart: lines (one or more series) or bars. Tap to read a point. */
export function Chart(p: Props) {
  const id = useId();
  const svgRef = useRef<SVGSVGElement>(null);
  const [sel, setSel] = useState<number | null>(null);
  const W = 340, H = 132, L = 32, R = 10, T = 10, B = 22, iw = W - L - R, ih = H - T - B, n = p.labels.length;
  const vals = p.series.flatMap((s) => s.values).filter((v): v is number => v != null);
  if (!vals.length) return <div className="empty">Not enough data yet</div>;
  let lo = p.min ?? Math.min(...vals), hi = p.max ?? Math.max(...vals);
  if (p.ref != null) { lo = Math.min(lo, p.ref); hi = Math.max(hi, p.ref); }
  if (p.pad) { lo -= p.pad; hi += p.pad; }
  if (p.floor != null) lo = Math.max(p.floor, lo);
  if (p.ceil != null) hi = Math.min(p.ceil, hi);
  if (hi - lo < 1e-9) { lo -= 1; hi += 1; }
  const X = (i: number) => p.bars ? L + (i + 0.5) * iw / n : L + (n === 1 ? iw / 2 : i * iw / (n - 1));
  const Y = (v: number) => T + (1 - (v - lo) / (hi - lo)) * ih;
  const ticks = n <= 2 ? [...Array(n).keys()] : [0, Math.floor((n - 1) / 2), n - 1];

  const pick = (clientX: number) => {
    const r = svgRef.current!.getBoundingClientRect(), px = ((clientX - r.left) / r.width) * W;
    let best = -1, bd = Infinity;
    for (let i = 0; i < n; i++) if (p.series.some((s) => s.values[i] != null) && Math.abs(X(i) - px) < bd) { bd = Math.abs(X(i) - px); best = i; }
    if (best >= 0) setSel(best);
  };
  const readout = sel == null ? (n > 1 ? "Tap the chart to read a point." : "")
    : `${p.long?.[sel] ?? p.labels[sel]}: ` + p.series.map((s) => `${p.series.length > 1 ? s.name + " " : ""}${s.values[sel] == null ? "–" : p.fmt(s.values[sel]!)}`).join(", ");

  return <>
    <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} className="chart" role="img" aria-label={`${p.title} chart`} aria-describedby={id}
      onPointerDown={(e) => pick(e.clientX)}>
      {[lo, (lo + hi) / 2, hi].map((v, i) => <g key={i}>
        <line x1={L} x2={W - R} y1={Y(v)} y2={Y(v)} className="grid" />
        <text x={L - 5} y={Y(v) + 3.5} textAnchor="end" className="ax">{p.fmt(v)}</text>
      </g>)}
      {p.ref != null && <line x1={L} x2={W - R} y1={Y(p.ref)} y2={Y(p.ref)} className="ref" />}
      {ticks.map((i) => <text key={i} x={X(i)} y={H - 6} className="ax" textAnchor={n > 1 && i === 0 ? "start" : n > 1 && i === n - 1 ? "end" : "middle"}>{p.labels[i]}</text>)}
      {p.bars ? p.series[0].values.map((v, i) => v == null ? null :
        <rect key={i} x={X(i) - Math.max(2, (iw / n) * 0.66) / 2} y={Y(v)} width={Math.max(2, (iw / n) * 0.66)} height={Math.max(0, Y(lo) - Y(v))} rx={1.5} className={"bar" + (i === n - 1 ? " last" : "")} />)
        : p.series.map((s, si) => {
          let d = "", pen = false;
          s.values.forEach((v, i) => { if (v == null) { pen = false; return; } d += `${pen ? "L" : "M"}${X(i).toFixed(1)},${Y(v).toFixed(1)}`; pen = true; });
          return <g key={si}>
            <path d={d} className="ln" style={{ stroke: s.color }} />
            {s.values.map((v, i) => v == null ? null : <circle key={i} cx={X(i)} cy={Y(v)} r={i === n - 1 ? 4 : 2.5} style={{ fill: i === n - 1 && si === 0 ? "var(--c-accent)" : s.color }} />)}
          </g>;
        })}
      {sel != null && <line x1={X(sel)} x2={X(sel)} y1={T} y2={T + ih} className="cur" />}
    </svg>
    {p.series.length > 1 && <div className="legend">{p.series.map((s) => <span key={s.name}><i style={{ background: s.color }} />{s.name}</span>)}</div>}
    <div className="readout" id={id} aria-live="polite">{readout}</div>
  </>;
}
